/**
 * PKCE (Proof Key for Code Exchange) Utilities
 *
 * PKCE is an OAuth 2.0 security extension that prevents authorization code
 * interception attacks. It works by:
 * 1. Client generates random `code_verifier` (43-128 chars)
 * 2. Client creates `code_challenge` = base64url(sha256(code_verifier))
 * 3. Client sends code_challenge with authorization request
 * 4. Client sends code_verifier with token exchange request
 * 5. Server verifies: sha256(code_verifier) == code_challenge
 *
 * Reference: RFC 7636
 */

/**
 * Generate a cryptographically secure random code verifier
 * @returns Random string (128 characters, base64-URL encoded)
 */
export function generateCodeVerifier(): string {
  // Generate 96 random bytes (will become 128 chars when base64-URL encoded)
  const array = new Uint8Array(96)
  crypto.getRandomValues(array)

  return base64URLEncode(array)
}

/**
 * Generate a code challenge from a code verifier
 *
 * IMPORTANT: Zoom Apps In-Client OAuth ONLY supports PLAIN PKCE method!
 * Despite official documentation stating S256 is supported, Zoom staff confirmed:
 * "The client does not officially support the S256 codeChallenge, only plain at this time"
 *
 * @param verifier The code verifier
 * @returns The same verifier (PLAIN method - not hashed)
 * @see https://devforum.zoom.us/t/unable-to-exchange-token-during-in-client-oauth-flow-and-other-issues/77933
 */
export function generateCodeChallenge(verifier: string): string {
  // PLAIN method: code_challenge = code_verifier (not hashed)
  // This is what Zoom Apps expects despite the documentation
  return verifier
}

/**
 * Base64-URL encode a byte array (RFC 4648 § 5)
 * Standard base64 but with URL-safe characters:
 * - Replace '+' with '-'
 * - Replace '/' with '_'
 * - Remove padding '='
 */
function base64URLEncode(bytes: Uint8Array): string {
  // Convert bytes to base64
  const base64 = btoa(String.fromCharCode(...bytes))

  // Make URL-safe
  return base64
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '')
}

/**
 * Generate a random state parameter for CSRF protection
 * @returns Random string (32 characters)
 */
export function generateState(): string {
  const array = new Uint8Array(24) // 24 bytes = 32 chars when base64-URL encoded
  crypto.getRandomValues(array)
  return base64URLEncode(array)
}
